// A fixed collation, rather than the reader's own. It keeps a letter carrying
// a diacritic next to the plain letter for every reader, and it matches the
// transliterated key the server sorts query names by. Danish collation would
// place Æ, Ø and Å after Z instead.
export const textCollator = new Intl.Collator('en')

// Sort a collection of elements that can be integers, floats and strings
// elements that can parse to floats will be ordered first, followed by strings
// Takes a collection (array of elements) and an optional accessor function to access the property of the element to be sorted on
//   example: mixedSort(collection, (o) => o.filter_values[uuid].sort_value )
export const mixedSort = <A>(
  collection: Array<A>,
  accessor = <O>(o: O): O => o,
  priorityFn = <O>(_o: O): boolean => false
): Array<A> => {
  // First compare as floats, then as strings.
  return collection.sort((a, b) => {
    if (priorityFn) {
      if (priorityFn(a)) {
        return -1
      }

      if (priorityFn(b)) {
        return 1
      }
    }

    // If we want to make this re-usable we should de-couple the next 2 lines
    const sortValueA = accessor(a)
    const sortValueB = accessor(b)

    const aIsNullish =
      sortValueA === null || sortValueA === undefined || sortValueA === ''
    const bIsNullish =
      sortValueB === null || sortValueB === undefined || sortValueB === ''

    if (aIsNullish && !bIsNullish) {
      return -1
    }
    if (!aIsNullish && bIsNullish) {
      return 1
    }
    if (aIsNullish && bIsNullish) {
      return 0
    }

    const floatA = typeof sortValueA === 'string' ? parseFloat(sortValueA) : NaN
    const floatB = typeof sortValueB === 'string' ? parseFloat(sortValueB) : NaN
    // Both parse as numbers, and are different
    if (floatA && floatB && floatA !== floatB) {
      return floatA - floatB
    }
    // A parses as float, but B doesn't
    if (floatA && isNaN(floatB)) {
      return -1
    }
    // B parses as float, but A doesn't
    if (isNaN(floatA) && floatB) {
      return 1
    }
    // Two numbers keep a numeric comparison. The collator reads them as text,
    // which orders 10 before 9.
    if (typeof sortValueA === 'number' && typeof sortValueB === 'number') {
      return sortValueA - sortValueB
    }
    return textCollator.compare(String(sortValueA), String(sortValueB))
  })
}
